const cookie = require('js-cookie')
const uuid = require('node-uuid')
const qs = require('qs')
const _ = require('lodash')
const { track } = require('client/lib/analytics')

const SESS_EXPIRE = 30 * 60 * 1000 // 30 min

module.exports = class Session {
  constructor(user) {
    this.user = user
    this.isUserReal = user && !user.bot
    this.initialTempId = cookie.get('temp_id')
  }

  /**
   * update-and-return the analytics session id based on state given to constructor
   * @returns {string} the session id for analytics
   */
  update() {
    if (!this.isUserReal) {
      return
    }

    const { id: userId, loggedIn } = this.user
    const tempId = cookie.get('temp_id')
    const userSession = getSessionId(userId)
    const tempSession = tempId && getSessionId(tempId)

    // Clear the anonymous user ID after login
    // Don't clear anonymous IDs from other/background tabs
    if (loggedIn && tempId && tempId === this.initialTempId) {
      cookie.remove('temp_id')
    }

    // The current session (if any) will get renewed
    clearAllSessions()

    if (userSession) {
      // Renew alive session without tracking the change event.
      return setSessionId(userId, userSession, false)
    }
    if (tempSession) {
      // Renew alive session and track the change event if the user logged-in.
      return setSessionId(userId, tempSession, loggedIn)
    }
    // Session is expired or doesn't exist.
    // Create new session and track the change if user is now logged-in.
    return setSessionId(userId, uuid.v4(), loggedIn)
  }

  /**
   * add listeners to test the validity of the current analytics session each time the document gains or loses focus
   * @param {function} sessionChangedCallback
   * @returns {function} remove event listeners and stop watching
   */
  watch(sessionChangedCallback) {
    if (!this.isUserReal) {
      return _.noop
    }

    const userId = this.user.id
    let mostRecentSessionId = getSessionId(userId) // capture the session at initial page load
    $(window).on('focus blur', windowFocusEventHandler)
    return function () {
      $(window).off('focus blur', windowFocusEventHandler)
    }

    function windowFocusEventHandler() {
      const currentSessionId = getSessionId(userId)   // capture the session id on focus/blur
      if (currentSessionId !== mostRecentSessionId) { // the session may have been affected by other tabs or ttl timeout
        sessionChangedCallback()
        mostRecentSessionId = currentSessionId
      }
    }
  }
}

/**
 * return the id of the given user's session as maintained client-side by this module using the `usr_sess:<userid>` cookie
 * @param {string} userId
 * @returns {string|boolean} id of the session as determined in the `update()` method or `false` if not valid
 */
function getSessionId(userId) {
  // Return `false` if the session does not exist or is expired.
  const session = cookie.getJSON('usr_sess:' + userId)
  const ttl = _.toSafeInteger(_.get(session, 'ttl'))
  return ttl > Date.now() && _.get(session, 'id')
}

/**
 * create-or-update the `usr_sess:<userid>` cookie. this method is agnostic with respect to the authentication state of the userId
 * @param {string} userId used to form the cookie's key. expect either the logged-in user's id or a temp id
 * @param {string} sessionId saved as the value of the id
 * @param {boolean} trackSessionChanged flag to send a `session-changed` event to analytics tracking
 * @returns {string} the given sessionId, unchanged
 */
function setSessionId(userId, sessionId, trackSessionChanged) {
  cookie.set('usr_sess:' + userId, {
    id: sessionId,
    ttl: Date.now() + SESS_EXPIRE
  }, {
    // Set a session-based cookie which gets cleared on browser close.
    expires: null
  })

  if (getSessionId(userId) === sessionId) {
    // Don't send tracking if the cookie did not persist.
    if (trackSessionChanged) {
      track('session-changed', { sessionId, noThirdPartyTracking: true })
    }
    trackUtmParams(sessionId)
  }
  return sessionId
}

function clearAllSessions() {
  for (let cookieName in cookie.get()) {
    if (cookieName.startsWith('usr_sess:')) {
      cookie.remove(cookieName)
    }
  }
}

// trackUtmParams global state
let previousUtmQuery
let previousUtmParams

function trackUtmParams(sessionId) {
  const queryString = location.search
  if (!queryString || queryString === previousUtmQuery) {
    // Skip parsing if this query string was previously tracked
    return
  }
  previousUtmQuery = queryString
  const utmParams = parseUtmParams(queryString)
  if (!utmParams || _.isEqual(utmParams, previousUtmParams)) {
    // Skip tracking if these UTM parameters were previously tracked
    return
  }
  previousUtmParams = utmParams
  track('session-update', {
    sessionId,
    utmParams,
    noThirdPartyTracking: true
  })
}

function parseUtmParams(queryString) {
  const utmPrefix = 'utm_'
  const customPrefix = utmPrefix + 'mix_'
  const customParams = {}
  const queryParams = qs.parse(queryString, { ignoreQueryPrefix: true })
  const utmParams = _.transform(queryParams, (obj, value, key) => {
    if (!key.startsWith(utmPrefix)) {
      // Ignore non-UTM query parameters
      return
    }
    if (key.startsWith(customPrefix)) {
      const customKeySuffix = key.slice(customPrefix.length)
      customParams[customKeySuffix] = value
    } else {
      obj[key] = value
    }
  })
  if (!_.isEmpty(customParams)) {
    utmParams['utm_custom_params'] = customParams
  }
  return _.isEmpty(utmParams) ? undefined : utmParams
}
